import React, { useState, useCallback } from 'react';
import type { 
  ComponentParameter, 
  ValidationRule, 
  ConditionalRule, 
  DynamicContentRule 
} from '../types/template';
import './ComponentParameters.css';

// Логи оставляем только для разработки, чтобы не засорять production и тесты.
const isDev = process.env.NODE_ENV === 'development';
const devLog = (...args: unknown[]) => {
  if (isDev) console.log(...args);
};

const PARAMETER_TYPES: Array<{ value: ComponentParameter['type']; label: string }> = [
  { value: 'text', label: 'Текст' },
  { value: 'number', label: 'Число' },
  { value: 'boolean', label: 'Да/Нет' },
  { value: 'select', label: 'Выбор из списка' },
  { value: 'multiselect', label: 'Множественный выбор' },
  { value: 'color', label: 'Цвет' },
  { value: 'image', label: 'Изображение' },
  { value: 'json', label: 'JSON' }
];

const OPERATORS: Array<{ value: ConditionalRule['operator']; label: string }> = [
  { value: 'equals', label: 'Равно' },
  { value: 'not_equals', label: 'Не равно' },
  { value: 'contains', label: 'Содержит' },
  { value: 'greater_than', label: 'Больше чем' },
  { value: 'less_than', label: 'Меньше чем' }
];

const ACTIONS: Array<{ value: ConditionalRule['action']; label: string }> = [
  { value: 'show', label: 'Показать' },
  { value: 'hide', label: 'Скрыть' },
  { value: 'enable', label: 'Включить' },
  { value: 'disable', label: 'Отключить' },
  { value: 'set_value', label: 'Установить значение' }
];

interface ComponentParametersProps {
  parameters: ComponentParameter[];
  validationRules: ValidationRule[];
  conditionalRules: ConditionalRule[];
  dynamicContentRules: DynamicContentRule[];
  onParametersChange: (parameters: ComponentParameter[]) => void;
  onValidationRulesChange: (rules: ValidationRule[]) => void;
  onConditionalRulesChange: (rules: ConditionalRule[]) => void;
  onDynamicContentRulesChange: (rules: DynamicContentRule[]) => void;
}

const ComponentParameters: React.FC<ComponentParametersProps> = ({
  parameters,
  validationRules,
  conditionalRules,
  dynamicContentRules,
  onParametersChange,
  onValidationRulesChange,
  onConditionalRulesChange,
  onDynamicContentRulesChange
}) => {
  const [activeTab, setActiveTab] = useState<'parameters' | 'validation' | 'conditional' | 'dynamic'>('parameters');
  const [editingParameter, setEditingParameter] = useState<ComponentParameter | null>(null);
  const [editingValidationRule, setEditingValidationRule] = useState<ValidationRule | null>(null);
  const [editingConditionalRule, setEditingConditionalRule] = useState<ConditionalRule | null>(null);
  const [editingDynamicRule, setEditingDynamicRule] = useState<DynamicContentRule | null>(null);

  const handleAddParameter = useCallback(() => {
    try {
      devLog('handleAddParameter: Starting to add parameter');
      const newParameter: ComponentParameter = {
        name: '',
        type: 'text',
        label: '',
        description: '',
        required: false,
        placeholder: '',
        options: []
      };
      devLog('handleAddParameter: Created parameter:', newParameter);
      setEditingParameter(newParameter);
      devLog('handleAddParameter: Parameter set successfully');
    } catch (error) {
      console.error('handleAddParameter: Error occurred:', error);
    }
  }, []);

  const handleSaveParameter = useCallback(() => {
    if (!editingParameter) return;

    if (editingParameter.name.trim() === '') {
      alert('Название параметра обязательно');
      return;
    }

    if (editingParameter.label.trim() === '') {
      alert('Метка параметра обязательна');
      return;
    }

    const updatedParameters = editingParameter.id
      ? parameters.map(p => p.id === editingParameter.id ? editingParameter : p)
      : [...parameters, { ...editingParameter, id: Date.now() }];

    onParametersChange(updatedParameters);
    setEditingParameter(null);
  }, [editingParameter, parameters, onParametersChange]);

  const handleDeleteParameter = useCallback((id: number) => {
    if (window.confirm('Удалить параметр?')) {
      const updatedParameters = parameters.filter(p => p.id !== id);
      onParametersChange(updatedParameters);
    }
  }, [parameters, onParametersChange]);

  const handleAddValidationRule = useCallback(() => {
    try {
      devLog('handleAddValidationRule: Starting to add validation rule');
      const newRule: ValidationRule = {
        parameter_name: parameters[0]?.name || '',
        rule_type: 'required_if',
        rule_value: '',
        error_message: ''
      };
      devLog('handleAddValidationRule: Created rule:', newRule);
      setEditingValidationRule(newRule);
      devLog('handleAddValidationRule: Validation rule set successfully');
    } catch (error) {
      console.error('handleAddValidationRule: Error occurred:', error);
    }
  }, [parameters]);

  const handleSaveValidationRule = useCallback(() => {
    if (!editingValidationRule) return;

    if (editingValidationRule.parameter_name.trim() === '') {
      alert('Выберите параметр');
      return;
    }

    if (editingValidationRule.error_message.trim() === '') {
      alert('Сообщение об ошибке обязательно');
      return;
    }

    const updatedRules = editingValidationRule.id
      ? validationRules.map(r => r.id === editingValidationRule.id ? editingValidationRule : r)
      : [...validationRules, { ...editingValidationRule, id: Date.now() }];

    onValidationRulesChange(updatedRules);
    setEditingValidationRule(null);
  }, [editingValidationRule, validationRules, onValidationRulesChange]);

  const handleDeleteValidationRule = useCallback((id: number) => {
    if (window.confirm('Удалить правило валидации?')) {
      const updatedRules = validationRules.filter(r => r.id !== id);
      onValidationRulesChange(updatedRules);
    }
  }, [validationRules, onValidationRulesChange]);

  const handleAddConditionalRule = useCallback(() => {
    const newRule: ConditionalRule = {
      condition: '',
      condition_type: 'if',
      parameter_name: parameters[0]?.name || '',
      operator: 'equals',
      value: '',
      action: 'show',
      target_parameter: parameters[0]?.name || ''
    };
    setEditingConditionalRule(newRule);
  }, [parameters]);

  const handleSaveConditionalRule = useCallback(() => {
    if (!editingConditionalRule) return;

    if (editingConditionalRule.condition.trim() === '') {
      alert('Условие обязательно');
      return;
    }

    if (!editingConditionalRule.target_parameter || editingConditionalRule.target_parameter.trim() === '') {
      alert('Выберите целевой параметр');
      return;
    }

    const updatedRules = editingConditionalRule.id
      ? conditionalRules.map(r => r.id === editingConditionalRule.id ? editingConditionalRule : r)
      : [...conditionalRules, { ...editingConditionalRule, id: Date.now() }];

    onConditionalRulesChange(updatedRules);
    setEditingConditionalRule(null);
  }, [editingConditionalRule, conditionalRules, onConditionalRulesChange]);

  const handleDeleteConditionalRule = useCallback((id: number) => {
    if (window.confirm('Удалить условное правило?')) {
      const updatedRules = conditionalRules.filter(r => r.id !== id);
      onConditionalRulesChange(updatedRules);
    }
  }, [conditionalRules, onConditionalRulesChange]);

  const handleAddDynamicRule = useCallback(() => {
    const newRule: DynamicContentRule = {
      rule_type: 'random',
      content_source: 'database',
      parameters: {},
      refresh_interval: 0
    };
    setEditingDynamicRule(newRule);
  }, []);

  const handleSaveDynamicRule = useCallback(() => {
    if (!editingDynamicRule) return;

    if (!editingDynamicRule.parameters || Object.keys(editingDynamicRule.parameters).length === 0) {
      alert('Параметры обязательны');
      return;
    }

    const updatedRules = editingDynamicRule.id
      ? dynamicContentRules.map(r => r.id === editingDynamicRule.id ? editingDynamicRule : r)
      : [...dynamicContentRules, { ...editingDynamicRule, id: Date.now() }];

    onDynamicContentRulesChange(updatedRules);
    setEditingDynamicRule(null);
  }, [editingDynamicRule, dynamicContentRules, onDynamicContentRulesChange]);

  const handleDeleteDynamicRule = useCallback((id: number) => {
    if (window.confirm('Удалить правило динамического контента?')) {
      const updatedRules = dynamicContentRules.filter(r => r.id !== id);
      onDynamicContentRulesChange(updatedRules);
    }
  }, [dynamicContentRules, onDynamicContentRulesChange]);

  const renderParameterForm = () => (
    <div className="parameter-form" onClick={(e) => e.stopPropagation()}>
      <h3 onClick={(e) => e.stopPropagation()}>{editingParameter?.id ? 'Редактировать параметр' : 'Новый параметр'}</h3>
      
      <div className="form-row">
        <div className="form-group" onClick={(e) => e.stopPropagation()}>
          <label onClick={(e) => e.stopPropagation()}>Название параметра *</label>
          <input
            type="text"
            value={editingParameter?.name || ''}
            onChange={(e) => setEditingParameter(prev => prev ? { ...prev, name: e.target.value } : null)}
            onClick={(e) => e.stopPropagation()}
            placeholder="internal_name"
            className="form-control"
          />
        </div>
        
        <div className="form-group">
          <label>Тип *</label>
          <select
            value={editingParameter?.type || 'text'}
            onChange={(e) => setEditingParameter(prev => prev ? { ...prev, type: e.target.value as ComponentParameter['type'] } : null)}
            onClick={(e) => e.stopPropagation()}
            className="form-control"
          >
            {PARAMETER_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Метка *</label>
          <input
            type="text"
            value={editingParameter?.label || ''}
            onChange={(e) => setEditingParameter(prev => prev ? { ...prev, label: e.target.value } : null)}
            onClick={(e) => e.stopPropagation()}
            placeholder="Отображаемое название"
            className="form-control"
          />
        </div>
        
        <div className="form-group">
          <label>Описание</label>
          <input
            type="text"
            value={editingParameter?.description || ''}
            onChange={(e) => setEditingParameter(prev => prev ? { ...prev, description: e.target.value } : null)}
            onClick={(e) => e.stopPropagation()}
            placeholder="Описание параметра"
            className="form-control"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Значение по умолчанию</label>
          <input
            type="text"
            value={editingParameter?.default_value || ''}
            onChange={(e) => setEditingParameter(prev => prev ? { ...prev, default_value: e.target.value } : null)}
            onClick={(e) => e.stopPropagation()}
            placeholder="Значение по умолчанию"
            className="form-control"
          />
        </div>
        
        <div className="form-group">
          <label>Placeholder</label>
          <input
            type="text"
            value={editingParameter?.placeholder || ''}
            onChange={(e) => setEditingParameter(prev => prev ? { ...prev, placeholder: e.target.value } : null)}
            onClick={(e) => e.stopPropagation()}
            placeholder="Подсказка в поле"
            className="form-control"
          />
        </div>
      </div>

      {(editingParameter?.type === 'number') && (
        <div className="form-row" onClick={(e) => e.stopPropagation()}>
          <div className="form-group">
            <label>Минимальное значение</label>
            <input
              type="number"
              value={editingParameter?.min_value || ''}
              onChange={(e) => setEditingParameter(prev => prev ? { ...prev, min_value: Number(e.target.value) } : null)}
              onClick={(e) => e.stopPropagation()}
              className="form-control"
            />
          </div>
          
          <div className="form-group">
            <label>Максимальное значение</label>
            <input
              type="number"
              value={editingParameter?.max_value || ''}
              onChange={(e) => setEditingParameter(prev => prev ? { ...prev, max_value: Number(e.target.value) } : null)}
              onClick={(e) => e.stopPropagation()}
              className="form-control"
            />
          </div>
          
          <div className="form-group">
            <label>Шаг</label>
            <input
              type="number"
              value={editingParameter?.step || ''}
              onChange={(e) => setEditingParameter(prev => prev ? { ...prev, step: Number(e.target.value) } : null)}
              onClick={(e) => e.stopPropagation()}
              className="form-control"
            />
          </div>
        </div>
      )}

      {(editingParameter?.type === 'select' || editingParameter?.type === 'multiselect') && (
        <div className="form-group">
          <label>Варианты выбора</label>
          <div className="options-list">
            {(editingParameter?.options || []).map((option, index) => (
              <div key={index} className="option-item">
                <input
                  type="text"
                  value={option.value}
                  onChange={(e) => {
                    const newOptions = [...(editingParameter?.options || [])];
                    newOptions[index] = { ...option, value: e.target.value };
                    setEditingParameter(prev => prev ? { ...prev, options: newOptions } : null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Значение"
                  className="form-control"
                />
                <input
                  type="text"
                  value={option.label}
                  onChange={(e) => {
                    const newOptions = [...(editingParameter?.options || [])];
                    newOptions[index] = { ...option, label: e.target.value };
                    setEditingParameter(prev => prev ? { ...prev, options: newOptions } : null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Метка"
                  className="form-control"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newOptions = (editingParameter?.options || []).filter((_, i) => i !== index);
                    setEditingParameter(prev => prev ? { ...prev, options: newOptions } : null);
                  }}
                  className="btn btn-danger btn-sm"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const newOptions = [...(editingParameter?.options || []), { value: '', label: '' }];
                setEditingParameter(prev => prev ? { ...prev, options: newOptions } : null);
              }}
              className="btn btn-secondary btn-sm"
            >
              + Добавить вариант
            </button>
          </div>
        </div>
      )}

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={editingParameter?.required || false}
            onChange={(e) => setEditingParameter(prev => prev ? { ...prev, required: e.target.checked } : null)}
            onClick={(e) => e.stopPropagation()}
          />
          Обязательный параметр
        </label>
      </div>

      <div className="form-actions" onClick={(e) => e.stopPropagation()}>
        <button onClick={(e) => {
          e.stopPropagation();
          handleSaveParameter();
        }} className="btn btn-primary">
          Сохранить
        </button>
        <button onClick={(e) => {
          e.stopPropagation();
          setEditingParameter(null);
        }} className="btn btn-secondary">
          Отмена
        </button>
      </div>
    </div>
  );

  const renderValidationRuleForm = () => (
    <div className="validation-rule-form">
      <h3>{editingValidationRule?.id ? 'Редактировать правило' : 'Новое правило валидации'}</h3>
      
      <div className="form-row">
        <div className="form-group">
          <label>Параметр *</label>
          <select
            value={editingValidationRule?.parameter_name || ''}
            onChange={(e) => setEditingValidationRule(prev => prev ? { ...prev, parameter_name: e.target.value } : null)}
            onClick={(e) => e.stopPropagation()}
            className="form-control"
          >
            <option value="">Выберите параметр</option>
            {parameters.map(param => (
              <option key={param.name} value={param.name}>{param.label}</option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label>Тип правила *</label>
          <select
            value={editingValidationRule?.rule_type || 'required_if'}
            onChange={(e) => setEditingValidationRule(prev => prev ? { ...prev, rule_type: e.target.value as ValidationRule['rule_type'] } : null)}
            onClick={(e) => e.stopPropagation()}
            className="form-control"
          >
            <option value="required_if">Обязателен если</option>
            <option value="pattern">Регулярное выражение</option>
            <option value="range">Диапазон значений</option>
            <option value="custom">Пользовательская валидация</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Значение правила</label>
          <input
            type="text"
            value={editingValidationRule?.rule_value || ''}
            onChange={(e) => setEditingValidationRule(prev => prev ? { ...prev, rule_value: e.target.value } : null)}
            onClick={(e) => e.stopPropagation()}
            placeholder="Зависит от типа правила"
            className="form-control"
          />
        </div>
        
        <div className="form-group">
          <label>Зависит от</label>
          <select
            value={editingValidationRule?.depends_on || ''}
            onChange={(e) => setEditingValidationRule(prev => prev ? { ...prev, depends_on: e.target.value } : null)}
            className="form-control"
          >
            <option value="">Нет зависимости</option>
            {parameters.map(param => (
              <option key={param.name} value={param.name}>{param.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-group">
        <label>Сообщение об ошибке *</label>
        <input
          type="text"
          value={editingValidationRule?.error_message || ''}
          onChange={(e) => setEditingValidationRule(prev => prev ? { ...prev, error_message: e.target.value } : null)}
          placeholder="Текст ошибки валидации"
          className="form-control"
        />
      </div>

      <div className="form-actions">
        <button onClick={(e) => {
          e.stopPropagation();
          handleSaveValidationRule();
        }} className="btn btn-primary">
          Сохранить
        </button>
        <button onClick={(e) => {
          e.stopPropagation();
          setEditingValidationRule(null);
        }} className="btn btn-secondary">
          Отмена
        </button>
      </div>
    </div>
  );

  const renderConditionalRuleForm = () => (
    <div className="conditional-rule-form">
      <h3>{editingConditionalRule?.id ? 'Редактировать правило' : 'Новое условное правило'}</h3>
      
      <div className="form-row">
        <div className="form-group">
          <label>Условие *</label>
          <input
            type="text"
            value={editingConditionalRule?.condition || ''}
            onChange={(e) => setEditingConditionalRule(prev => prev ? { ...prev, condition: e.target.value } : null)}
            placeholder="Логическое выражение"
            className="form-control"
          />
        </div>
        
        <div className="form-group">
          <label>Действие *</label>
          <select
            value={editingConditionalRule?.action || 'show'}
            onChange={(e) => setEditingConditionalRule(prev => prev ? { ...prev, action: e.target.value as ConditionalRule['action'] } : null)}
            className="form-control"
          >
            {ACTIONS.map(action => (
              <option key={action.value} value={action.value}>{action.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Целевой параметр *</label>
          <select
            value={editingConditionalRule?.target_parameter || ''}
            onChange={(e) => setEditingConditionalRule(prev => prev ? { ...prev, target_parameter: e.target.value } : null)}
            className="form-control"
          >
            <option value="">Выберите параметр</option>
            {parameters.map(param => (
              <option key={param.name} value={param.name}>{param.label}</option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label>Оператор</label>
          <select
            value={editingConditionalRule?.operator || 'equals'}
            onChange={(e) => setEditingConditionalRule(prev => prev ? { ...prev, operator: e.target.value as ConditionalRule['operator'] } : null)}
            className="form-control"
          >
            {OPERATORS.map(op => (
              <option key={op.value} value={op.value}>{op.value}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-group">
        <label>Значение</label>
        <input
          type="text"
          value={editingConditionalRule?.value || ''}
          onChange={(e) => setEditingConditionalRule(prev => prev ? { ...prev, value: e.target.value } : null)}
          placeholder="Значение для сравнения"
          className="form-control"
        />
      </div>

      <div className="form-actions">
        <button onClick={(e) => {
          e.stopPropagation();
          handleSaveConditionalRule();
        }} className="btn btn-primary">
          Сохранить
        </button>
        <button onClick={(e) => {
          e.stopPropagation();
          setEditingConditionalRule(null);
        }} className="btn btn-secondary">
          Отмена
        </button>
      </div>
    </div>
  );

  const renderDynamicRuleForm = () => (
    <div className="dynamic-rule-form">
      <h3>{editingDynamicRule?.id ? 'Редактировать правило' : 'Новое правило динамического контента'}</h3>
      
      <div className="form-row">
        <div className="form-group">
          <label>Тип правила *</label>
          <select
            value={editingDynamicRule?.rule_type || 'random'}
            onChange={(e) => setEditingDynamicRule(prev => prev ? { ...prev, rule_type: e.target.value as DynamicContentRule['rule_type'] } : null)}
            className="form-control"
          >
            <option value="random">Случайный</option>
            <option value="sequential">Последовательный</option>
            <option value="weighted">Взвешенный</option>
            <option value="conditional">Условный</option>
          </select>
        </div>
        
        <div className="form-group">
          <label>Источник контента *</label>
          <select
            value={editingDynamicRule?.content_source || 'database'}
            onChange={(e) => setEditingDynamicRule(prev => prev ? { ...prev, content_source: e.target.value as DynamicContentRule['content_source'] } : null)}
            className="form-control"
          >
            <option value="database">База данных</option>
            <option value="api">API</option>
            <option value="generated">Сгенерированный</option>
            <option value="template">Шаблон</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label>Параметры *</label>
        <textarea
          value={JSON.stringify(editingDynamicRule?.parameters || {}, null, 2)}
          onChange={(e) => {
            try {
              const params = JSON.parse(e.target.value);
              setEditingDynamicRule(prev => prev ? { ...prev, parameters: params } : null);
            } catch (error) {
              // Игнорируем ошибки парсинга JSON
            }
          }}
          placeholder='{"key": "value"}'
          className="form-control"
          rows={4}
        />
      </div>

      <div className="form-group">
        <label>Интервал обновления (сек)</label>
        <input
          type="number"
          value={editingDynamicRule?.refresh_interval || 0}
          onChange={(e) => setEditingDynamicRule(prev => prev ? { ...prev, refresh_interval: parseInt(e.target.value) || 0 } : null)}
          placeholder="0"
          className="form-control"
        />
      </div>

      <div className="form-actions">
        <button onClick={(e) => {
          e.stopPropagation();
          handleSaveDynamicRule();
        }} className="btn btn-primary">
          Сохранить
        </button>
        <button onClick={(e) => {
          e.stopPropagation();
          setEditingDynamicRule(null);
        }} className="btn btn-secondary">
          Отмена
        </button>
      </div>
    </div>
  );

  devLog('ComponentParameters render:', { activeTab, parameters: parameters.length, validationRules: validationRules.length, conditionalRules: conditionalRules.length, dynamicContentRules: dynamicContentRules.length });

  // Полная изоляция компонента от внешних событий
  const handleComponentClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    devLog('ComponentParameters: Click isolated');
  }, []);

  const handleTabClick = useCallback((tabName: 'parameters' | 'validation' | 'conditional' | 'dynamic') => {
    devLog(`Switching to ${tabName} tab`);
    setActiveTab(tabName);
  }, []);

  return (
    <div 
      className="component-parameters" 
      onClick={handleComponentClick}
      style={{ 
        isolation: 'isolate',
        contain: 'layout style paint',
        position: 'relative',
        zIndex: 1000
      }}
    >
      <div className="parameters-tabs">
        <button
          type="button"
          className={`tab-button ${activeTab === 'parameters' ? 'active' : ''}`}
          onClick={() => handleTabClick('parameters')}
        >
          Параметры ({parameters.length})
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'validation' ? 'active' : ''}`}
          onClick={() => handleTabClick('validation')}
        >
          Валидация ({validationRules.length})
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'conditional' ? 'active' : ''}`}
          onClick={() => handleTabClick('conditional')}
        >
          Условия ({conditionalRules.length})
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'dynamic' ? 'active' : ''}`}
          onClick={() => handleTabClick('dynamic')}
        >
          Динамический контент ({dynamicContentRules.length})
        </button>
      </div>

      <div className="parameters-content">
        {activeTab === 'parameters' && (
          <div className="parameters-tab">
            <div className="tab-header">
              <h3>Параметры компонента</h3>
              <button 
                type="button"
                onClick={(e) => {
                  devLog('=== ADD PARAMETER BUTTON CLICKED ===');
                  devLog('Event target:', e.target);
                  devLog('Event currentTarget:', e.currentTarget);
                  devLog('Event type:', e.type);
                  e.stopPropagation();
                  e.preventDefault();
                  
                  // Используем setTimeout чтобы выполнить после текущего event loop
                  setTimeout(() => {
                    devLog('Executing handleAddParameter in setTimeout');
                    handleAddParameter();
                  }, 0);
                }}
                className="btn btn-primary"
              >
                + Добавить параметр
              </button>
            </div>

            {editingParameter ? (
              renderParameterForm()
            ) : (
              <div className="parameters-list" onClick={(e) => e.stopPropagation()}>
                {parameters.length === 0 ? (
                  <p className="no-parameters">Параметры не добавлены</p>
                ) : (
                  parameters.map(param => (
                    <div key={param.id} className="parameter-item">
                      <div className="parameter-info">
                        <div className="parameter-header">
                          <span className="parameter-name">{param.name}</span>
                          <span className="parameter-type">{param.type}</span>
                          {param.required && <span className="required-badge">Обязательный</span>}
                        </div>
                        <div className="parameter-details">
                          <span className="parameter-label">{param.label}</span>
                          {param.description && <span className="parameter-description">{param.description}</span>}
                        </div>
                      </div>
                      <div className="parameter-actions">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingParameter(param);
                          }}
                          className="btn btn-sm btn-edit"
                          title="Редактировать"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteParameter(param.id!);
                          }}
                          className="btn btn-sm btn-delete"
                          title="Удалить"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'validation' && (
          <div className="validation-tab">
            <div className="tab-header">
              <h3>Правила валидации</h3>
              <button 
                type="button"
                onClick={(e) => {
                  devLog('=== ADD VALIDATION RULE BUTTON CLICKED ===');
                  devLog('Event target:', e.target);
                  devLog('Event currentTarget:', e.currentTarget);
                  devLog('Event type:', e.type);
                  e.stopPropagation();
                  e.preventDefault();
                  
                  // Используем setTimeout чтобы выполнить после текущего event loop
                  setTimeout(() => {
                    devLog('Executing handleAddValidationRule in setTimeout');
                    handleAddValidationRule();
                  }, 0);
                }}
                className="btn btn-primary"
              >
                + Добавить правило
              </button>
            </div>

            {editingValidationRule ? (
              renderValidationRuleForm()
            ) : (
              <div className="validation-rules-list">
                {validationRules.length === 0 ? (
                  <p className="no-rules">Правила валидации не добавлены</p>
                ) : (
                  validationRules.map(rule => (
                    <div key={rule.id} className="validation-rule-item">
                      <div className="rule-info">
                        <div className="rule-header">
                          <span className="rule-parameter">{rule.parameter_name}</span>
                          <span className="rule-type">{rule.rule_type}</span>
                        </div>
                        <div className="rule-details">
                          <span className="rule-value">{rule.rule_value}</span>
                          {rule.depends_on && <span className="rule-dependency">Зависит от: {rule.depends_on}</span>}
                        </div>
                        <div className="rule-error">{rule.error_message}</div>
                      </div>
                      <div className="rule-actions">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingValidationRule(rule);
                          }}
                          className="btn btn-sm btn-edit"
                          title="Редактировать"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteValidationRule(rule.id!);
                          }}
                          className="btn btn-sm btn-delete"
                          title="Удалить"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'conditional' && (
          <div className="conditional-tab">
            <div className="tab-header">
              <h3>Условные правила</h3>
              <button 
                type="button"
                onClick={() => {
                  devLog('Add conditional rule button clicked');
                  handleAddConditionalRule();
                }}
                className="btn btn-primary"
              >
                + Добавить правило
              </button>
            </div>

            {editingConditionalRule ? (
              renderConditionalRuleForm()
            ) : (
              <div className="conditional-rules-list">
                {conditionalRules.length === 0 ? (
                  <p className="no-rules">Условные правила не добавлены</p>
                ) : (
                  conditionalRules.map(rule => (
                    <div key={rule.id} className="conditional-rule-item">
                      <div className="rule-info">
                        <div className="rule-header">
                          <span className="rule-condition">{rule.condition}</span>
                          <span className="rule-action">{rule.action}</span>
                        </div>
                        <div className="rule-details">
                          <span className="rule-target">{rule.target_parameter}</span>
                          <span className="rule-operator">{rule.operator}</span>
                          <span className="rule-value">{rule.value}</span>
                        </div>
                      </div>
                      <div className="rule-actions">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingConditionalRule(rule);
                          }}
                          className="btn btn-sm btn-edit"
                          title="Редактировать"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteConditionalRule(rule.id!);
                          }}
                          className="btn btn-sm btn-delete"
                          title="Удалить"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'dynamic' && (
          <div className="dynamic-tab">
            <div className="tab-header">
              <h3>Правила динамического контента</h3>
              <button 
                type="button"
                onClick={() => {
                  devLog('Add dynamic rule button clicked');
                  handleAddDynamicRule();
                }}
                className="btn btn-primary"
              >
                + Добавить правило
              </button>
            </div>

            {editingDynamicRule ? (
              renderDynamicRuleForm()
            ) : (
              <div className="dynamic-rules-list">
                {dynamicContentRules.length === 0 ? (
                  <p className="no-rules">Правила динамического контента не добавлены</p>
                ) : (
                  dynamicContentRules.map(rule => (
                    <div key={rule.id} className="dynamic-rule-item">
                      <div className="rule-info">
                        <div className="rule-header">
                          <span className="rule-rule-type">{rule.rule_type}</span>
                          <span className="rule-content-source">{rule.content_source}</span>
                        </div>
                        <div className="rule-parameters">{JSON.stringify(rule.parameters)}</div>
                        {rule.refresh_interval && rule.refresh_interval > 0 && <div className="rule-refresh">Обновление: {rule.refresh_interval}с</div>}
                      </div>
                      <div className="rule-actions">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingDynamicRule(rule);
                          }}
                          className="btn btn-sm btn-edit"
                          title="Редактировать"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDynamicRule(rule.id!);
                          }}
                          className="btn btn-sm btn-delete"
                          title="Удалить"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComponentParameters;
